







































































import theme from '@zk/theme/lib/variables.scss';
import {
  defineComponent,
  ref,
  computed,
  onMounted,
  watch
} from '@vue/composition-api';
import { useI18n } from 'common-module/src/util/useI18n';
import { usePromise } from 'common-module/src/util/usePromise';
import MemberFileGroupSelect from './TheMemberFileGroupSelect.vue';
import {
  PersonnelGroup,
  PersonnelItem,
  SelectOptionScope,
  DefaultPersonnelValue,
  transformSelectedForSubmit,
  transformArrayToString,
  transformStringToArray,
  usePersonnelData,
  useDefaultPersonnelData
} from './personnel';
import {
  getUserGroupDepartment
} from 'common-module/src/api/field';
import { isArray, isNil, uniq } from 'lodash';

export default defineComponent({
  name: 'PersonnelFileControl',
  components: { MemberFileGroupSelect },
  props: {
    isUpdate: {
      type: Boolean,
      default: false
    },
    projectId: {
      type: Number,
      default: null
    },
    personnelScope: {
      type: Array,
      default: () => []
    },
    multiple: {
      type: Boolean,
      default: false
    },
    defaultValue: {
      type: String,
      default: null
    }
  },
  setup(props, ctx) {
    const { t } = useI18n();

    // 初始化数据
    const {
      result: userData,
      refresh: loadDepartment,
      isLoading
    } = usePromise(
      async () => {
        const departments = await getUserGroupDepartment({ projectId: props.projectId }) || [];
        const personnelData = usePersonnelData(departments);
        return {
          departments,
          ...personnelData
        };
      },
      null,
      { immediate: false }
    );
    onMounted(() => loadDepartment());

    const userGroups = computed(() => userData.value?.userGroups || []);
    const ungroupedUsers = computed(() => userData.value?.ungroupedUsers || []);
    const allUsers = computed(() => userData.value?.allUsers || []);

    const personnelOptionsIds = computed(() => {
      const userIds = allUsers.value.map(u => u.value);
      const groupIds = userGroups.value.map(g => g.value);
      const unavailableProjectGroupIds = props.personnelScope
        .filter(item => item.selectedType === 3)
        .map(item => `project-member-group:${item.entityId}`);
      return uniq(['*', ...groupIds, ...unavailableProjectGroupIds, ...userIds]);
    });

    // 人员范围选择
    const selectedPersonnel = computed(
      () => {
        const selectedList: string[] = props.personnelScope
          .filter(Boolean)
          .map((item) => {
            if (item.selectedType === 2) {
              return '*';
            }
            const group = userGroups.value.find(candidate =>
              candidate.selectedType === item.selectedType && candidate.entityId === item.entityId?.toString()
            );
            if (group) {
              return group.value;
            }
            return item.selectedType === 3
              ? `project-member-group:${item.entityId}`
              : item.entityId?.toString();
          });

        return filterPersonnelScope({ ids: selectedList, options: personnelOptionsIds.value });
      }
    );

    // 过滤不在范围选择列表中的人员
    const filterPersonnelScope = ({ ids = [], options = [] }: {
      ids: string[],
      options: string[],
    }) => {
      if (isNil(options) || isNil(ids)) {
        return [];
      }
      return ids.filter(id => options.includes(id));
    };

    // 人员范围是否为全选
    const isSelectAll = computed(() => selectedPersonnel.value.includes('*'));

    const peopleSelectBind = computed(() => ({
      groups: [...userGroups.value],
      options: [...ungroupedUsers.value],
      multiple: true,
      placeholder: t('setting.memberFile.placeholder'),
      checkAllText: t('setting.memberFile.checkAllPro'),
      checkedAllIntText: t('setting.memberFile.proCheckAllInit'),
      checkedGroupIntText: t('setting.memberFile.checkGroupInit')
    }));

    const onPersonnelChange = (list: []) => {
      const res = transformSelectedForSubmit({ data: list, userGroups: userGroups.value });
      ctx.emit('personnel-scope-change', res);
    };
    watch(
      () => [selectedPersonnel.value, userData.value],
      ([newSelected, newUserData]) => {
        if (isNil(newSelected) || isNil(newUserData)) {
          return;
        }
        if (newSelected?.length !== props.personnelScope?.length) {
          onPersonnelChange(newSelected);
        }
        updateOptions(newSelected); // 更新默认人员选项列表
      }
    );


    // 单多选
    const multipleChoice = computed({
      get: () => props.multiple,
      set: (val) => ctx.emit('multiple-change', val)
    });


    // 默认人员选择列表
    const defaultPersonnel = computed({
      get: () => {
        return multipleChoice.value ? transformStringToArray(props.defaultValue) : props.defaultValue;
      },
      set: (val) => {
        const data = multipleChoice.value ? transformArrayToString(val) : val;
        ctx.emit('default-personnel-change', data);
      }
    });

    const defaultGroupOptions = ref<PersonnelGroup[]>([]);
    const defaultUngroupedOptions = ref<PersonnelItem[]>([]);
    const defaultOptionAll = ref<PersonnelItem[]>([]);

    // 更新默认人员选项列表
    const updateOptions = (list: Array<string>) => {
      const { defaultGroups, defaultUngrouped, allDefaultUsers } = useDefaultPersonnelData({
        sourceIds: [...list],
        userGroups: userGroups.value,
        allUsers: allUsers.value
      });
      defaultGroupOptions.value = isSelectAll.value ? [...userGroups.value] : defaultGroups;
      defaultUngroupedOptions.value = isSelectAll.value ? [...ungroupedUsers.value] : defaultUngrouped;
      defaultOptionAll.value = isSelectAll.value ? [...allUsers.value] : allDefaultUsers;
      setDefaultPersonnel(defaultPersonnel.value); // 更新默认人员选项值
    };

    // 设置默认人员选项值
    const setDefaultPersonnel = (data: DefaultPersonnelValue = []) => {
      defaultPersonnel.value = filterDefaultPersonnel({
        data,
        options: defaultOptionAll.value,
        isMultiple: multipleChoice.value
      });
    };

    // 过滤不在选项列表中的选项值
    const filterDefaultPersonnel = ({ data, options = [], isMultiple }:
    {
      data: DefaultPersonnelValue,
      options: PersonnelItem[],
      isMultiple: boolean
    }) => {
      return isMultiple && isArray(data)
        ? (data?.filter(data => options.some(o => o.value === data)) || [])
        : options.some(o => o.value === data) ? data : '';
    };

    const memberFileGroupBind = computed(() => {
      return {
        groups: defaultGroupOptions.value,
        options: defaultUngroupedOptions.value,
        placeholder: t('setting.memberFile.placeholder'),
        multiple: multipleChoice.value
      };
    });

    const showCollaboratorTag = (data: SelectOptionScope) => {
      if (!data) {
        return false;
      }
      return data.option?.projectRole === 'innerCollaborator';
    };

    const isDisabledUser = (data: SelectOptionScope) => {
      if (!data) {
        return false;
      }
      return data.option?.accountEnabled === 0;
    };


    return {
      isLoading,
      defaultPersonnel,
      multipleChoice,
      selectedPersonnel,
      peopleSelectBind,
      memberFileGroupBind,
      userData,
      onPersonnelChange,
      showCollaboratorTag,
      isDisabledUser
    };
  },
  data() {
    return {
      bemNS: theme.bemNS
    };
  }
});
